import { useEffect, useState, useCallback } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import { Layout } from './Layout';
import { CasesList } from './pages/CasesList';
import { CreateCase } from './pages/CreateCase';
import { CaseDetail } from './pages/CaseDetail';
import { AdminUsers } from './pages/AdminUsers';
import { AdminSpecialties } from './pages/AdminSpecialties';
import { Login } from './pages/Login';
import { getLang, type Lang } from './language';
import { useRoles, hasRole } from './useRoles';

interface CurrentUser {
  displayName: string;
}

export function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [lang, setLang] = useState<Lang>(getLang());
  const navigate = useNavigate();
  const [user, setUser] = useState<CurrentUser | null>(null);
  // Re-read roles whenever the signed-in user changes (roles aren't available until after login).
  const roles = useRoles([user?.displayName ?? null]);
  const isGlobalAdmin = hasRole(roles, 'GLOBAL_ADMIN');

  const loadUser = useCallback(async () => {
    try {
      await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      const name =
        (payload?.given_name && payload?.family_name
          ? `${payload.given_name} ${payload.family_name}`
          : (payload?.email as string)) ?? 'User';
      setUser({ displayName: name });
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  if (authLoading) {
    return (
      <Container className="d-flex align-items-center justify-content-center vh-100">
        <Spinner animation="border" role="status" className="me-2" />
        <span>Authenticating…</span>
      </Container>
    );
  }

  if (!user) return <Login onSignedIn={loadUser} />;

  return (
    <Layout userName={user.displayName} lang={lang} onLangChange={setLang} isGlobalAdmin={isGlobalAdmin}>
      <Routes>
        <Route path="/" element={<Navigate to="/cases" replace />} />
        <Route
          path="/cases"
          element={<CasesList onOpen={(id) => navigate(`/cases/${id}`)} />}
        />
        <Route
          path="/cases/new"
          element={
            <CreateCase
              defaultSourceLang={lang}
              onCreated={(id) => navigate(`/cases/${id}`)}
            />
          }
        />
        <Route path="/cases/:caseId" element={<CaseDetail />} />
        <Route
          path="/admin/users"
          element={isGlobalAdmin ? <AdminUsers /> : <Navigate to="/cases" replace />}
        />
        <Route
          path="/admin/specialties"
          element={isGlobalAdmin ? <AdminSpecialties /> : <Navigate to="/cases" replace />}
        />
        <Route path="*" element={<Navigate to="/cases" replace />} />
      </Routes>
    </Layout>
  );
}
